<template>
  <div
    class="book-tool"
    :class="[booktoolClasses, crossingElement ? 'contrast' : '']"
    @mouseover="open"
    ref="bookToolTable"
  >
    <div class="book-tool__btn portal pointer" ref="button" @click="openReservationModal()">
      <span>
        <div class="book-tool__btn-inner">{{ translations.bookTable }}</div>
      </span>
    </div>

    <div class="book-tool__popout" ref="popout">
      <div class="book-tool__popout-inner" ref="popoutContents">
        <radio-list-restaurants
          v-if="showLocation"
          :translations="translations"
          :location-options="locationOptions"
          class="mr4"
        >
          <template v-slot:trigger>
            <div class="bb bw1 b--white h-48px flex items-center" :class="`hover-b--${themeSite}`">
              <button class="bg-transparent bn outline-0 pointer">
                {{ locationValue }}
                <span>
                  <svg
                    class="v-mid"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                  >
                    <defs>
                      <path id="a" d="M17 15h4v2h-4v4h-2v-4h-4v-2h4v-4h2v4z" />
                    </defs>
                    <g fill="none" fill-rule="evenodd">
                      <mask id="b" fill="#fff">
                        <use xlink:href="#a" />
                      </mask>
                      <use fill="#D8D8D8" xlink:href="#a" />
                      <g :fill="themeColor" mask="url(#b)">
                        <path d="M0 0h32v32H0z" />
                      </g>
                    </g>
                  </svg>
                </span>
              </button>
            </div>
          </template>
        </radio-list-restaurants>
      </div>
    </div>
  </div>
</template>

<script>
import s from "../store"
import h from "../helpers"
import RadioListRestaurants from "./RadioListRestaurants.vue"
import Dropdown from "./Dropdown.vue"

export default {
  name: "BookTableTool",

  components: {
    Dropdown,
    RadioListRestaurants
  },

  data: function() {
    return {
      crossingElement: false,
      touched: false,
      isOpen: false,
      collapsedX: null,
      collapsedY: null,
      borderRadius: 46,
      collapsedBorderRadius: null,
      themeColor: "#963A1D"
    }
  },

  props: {
    locationOptions: { type: Array },
    showLocation: {
      type: Boolean,
      default: false
    },
    themeSite: String,
    translations: { type: Object }
  },

  created: function() {
    if (s.debug) console.log("created Vue component Book Table Tool")
  },

  mounted: function() {
    if (s.debug) console.log("mounted Vue component Book Table Tool")
    // set themeColor
    this.themeColor = s.static.theme[this.themeSite].colors.color2
    // set default location
    this.defaultLocation()
    // Calculate scales
    this.calculateCollapsedScale()
    this.collapsedBorderRadius = `${(
      this.borderRadius / this.collapsedX
    ).toFixed(6)}px/${this.borderRadius}px`
    // Collapse tool on initial page load
    this.close()

    // init pagePositionDetector
    this.pagePositionDetector()

    // If mobile view, break here
    if (this.isMobile) {
      return
    }
    // Inject animation styles into DOM
    const node = document.createElement("style")
    node.innerHTML = this.createKeyframeAnimation()
    document.body.appendChild(node)
    // Attach listeners
    window.addEventListener("resize", h.debounce(this.onResize, 200))
    window.addEventListener("load", () => {
      window.addEventListener("scroll", this.onScroll)
    })
    // Open book tool onload
    this.open()
  },

  watch: {
    isOpen(isOpen) {
      if (isOpen) {
        document.addEventListener("click", this.closeIfClickedOutside)
      }
    }
  },

  computed: {
    booktoolClasses: function() {
      if (this.touched && !this.isOpen) {
        return "book-tool--closed"
      } else if (this.isOpen) {
        return "book-tool--open"
      }
    },

    locationValue: function() {
      return s.state.bookingRestaurant.restaurant || "Location"
    },

    urlValue: function() {
      return s.state.bookingRestaurant.url || ""
    },

    formitableIdValue: function() {
      return s.state.bookingRestaurant.formitableId || ""
    },

    isMobile: function() {
      return window.innerWidth < 960
    }
  },

  methods: {
    defaultLocation: function() {
      if (s.debug) console.log("set default location")
      const capitalize = s => {
        if (typeof s !== "string") return ""
        return s.charAt(0).toUpperCase() + s.slice(1)
      }
      for (let i = 0; i < this.locationOptions.length; i++) {
        if (s.debug) console.log(this.locationOptions[i].location)

        if (
          this.locationOptions[i].location.indexOf(capitalize(this.themeSite)) >
          -1
        ) {
          s.state.bookingRestaurant.restaurant = this.locationOptions[i].title
          s.state.bookingRestaurant.url = this.locationOptions[i].url
        }
      }
    },

    onScroll(event) {
      // Close book tool when the users scrolls
      window.removeEventListener("scroll", this.onScroll)
      this.close()
    },

    onResize(event) {
      // Implement on resize
    },

    closeIfClickedOutside(event) {
      if (!event.target.closest(".book-tool")) {
        document.removeEventListener("click", this.closeIfClickedOutside)
        this.close()
      }
    },

    setPopoutInlineStyles: function({ borderRadius, outerX, innerX }) {
      this.$refs.popout.style.borderRadius = borderRadius
      this.$refs.popout.style.transform = `scaleX(${outerX})`
      this.$refs.popoutContents.style.transform = `scaleX(${innerX})`
    },

    open: function() {
      if (this.isMobile) {
        return
      }

      this.isOpen = true
      this.touched = true

      this.setPopoutInlineStyles({
        borderRadius: "46px",
        outerX: 1,
        innerX: 1
      })
    },

    close: function() {
      this.isOpen = false

      this.setPopoutInlineStyles({
        borderRadius: this.collapsedBorderRadius,
        outerX: this.collapsedX,
        innerX: 1 / this.collapsedX
      })
    },

    calculateCollapsedScale() {
      const collapsed = this.$refs.button.getBoundingClientRect()
      const expanded = this.$refs.popout.getBoundingClientRect()

      this.collapsedX = collapsed.width / expanded.width
      this.collapsedY = collapsed.height / expanded.height

      return {
        x: this.collapsedX,
        y: this.collapsedY
      }
    },

    createKeyframeAnimation() {
      const nFrames = 10
      const percentIncrement = 100 / nFrames

      const expandAnimation = []
      const expandContentsAnimation = []
      const collapseAnimation = []
      const collapseContentsAnimation = []

      for (let step = 0; step <= nFrames; step++) {
        let easedStep = this.ease(step / nFrames).toFixed(5)
        let percentage = (step * percentIncrement).toFixed(0)

        // Expand animation
        this.append({
          percentage,
          step: easedStep,
          startX: this.collapsedX,
          endX: 1,
          outerAnimation: expandAnimation,
          innerAnimation: expandContentsAnimation
        })

        // Collapse animation
        this.append({
          percentage,
          step: easedStep,
          startX: 1,
          endX: this.collapsedX,
          outerAnimation: collapseAnimation,
          innerAnimation: collapseContentsAnimation
        })
      }

      return `
          @keyframes popoutOut {
            ${expandAnimation.join("")}
          }

          @keyframes popoutContentsOut {
            ${expandContentsAnimation.join("")}
          }

          @keyframes popoutIn {
            ${collapseAnimation.join("")}
          }

          @keyframes popoutContentsIn {
            ${collapseContentsAnimation.join("")}
          }
        `
    },

    append({ percentage, step, startX, endX, outerAnimation, innerAnimation }) {
      // Calculate scales
      const xScale = (startX + (endX - startX) * parseFloat(step)).toFixed(5)
      const invScaleX = (1 / xScale).toFixed(5)

      outerAnimation.push(`
          ${percentage}% {
            transform: scaleX(${xScale});
            border-radius: ${46 / xScale}px/46px;
          }`)

      innerAnimation.push(`
          ${percentage}% {
            transform: scaleX(${invScaleX});
          }`)
    },

    ease(v, pow = 4) {
      return 1 - Math.pow(1 - v, pow)
    },

    openReservationModal() {
      if (s.state.bookingRestaurant.formitableId) {
        s.toggleRestaurantReservationModal(
          s.state.bookingRestaurant.formitableId
        )
      }
    },

    isInElement(buttonEl, targetEls) {
      const fixed_position =
        buttonEl.getBoundingClientRect().top + window.scrollY
      const fixed_height = buttonEl.offsetHeight

      this.crossingElement = false
      for (let i = 0; i < targetEls.length; i++) {
        const el = targetEls[i]
        const toCross_position = el.offsetTop
        const toCross_height = el.offsetHeight

        if (fixed_position + fixed_height < toCross_position) {
        } else if (fixed_position > toCross_position + toCross_height) {
        } else {
          this.crossingElement = true
        }
      }
    },

    pagePositionDetector() {
      this.$nextTick(() => {
        let els = document.querySelectorAll("[data-detect-section]")
        const buttonEl = this.$refs.bookToolTable
        const _self = this
        if (els.length > 0) {
          window.addEventListener(
            "scroll",
            function(event) {
              _self.isInElement(buttonEl, els)
            },
            false
          )
        }
      })
    }
  }
}
</script>

